import React, { useState } from 'react';
import {
  Card, Tabs, Descriptions, Tag, Button, Space, Typography,
  Table, Modal, Form, Input, Select, DatePicker, Upload,
  message, Popconfirm, Tooltip, Row, Col, Spin,
  InputNumber, Slider, Result, Empty
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined,
  FileTextOutlined, UndoOutlined, PlusOutlined,
  InboxOutlined, WarningOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import ConnectedProductsTab from './tabs/ConnectedProductsTab';
import CustomerTicketsTab from './tabs/CustomerTicketsTab';
import ShipmentPlanTab from './tabs/ShipmentPlanTab';
import ServiceScheduleTab from './tabs/ServiceScheduleTab';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ══════════════════════════════════════════════
//  Mock 数据
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

// 客户 Mock（与 CustomerList 保持一致）
const MOCK_CUSTOMERS = {
  'test-ent-001': {
    key: '1', enterpriseId: 'test-ent-001', enterpriseName: 'test-深圳智创科技有限公司', aliasName: '深圳智创',
    contactPerson: '张三', contactPhone: '13800001111', email: 'zhang@test-smart.com',
    authStatus: '已认证',
    background: '专注于智能家居领域的方案商，团队约50人',
    responsibleUserId: 'user-01', responsibleName: '李客服', responsibleAccount: 'lifuwu',
    weight: 95, status: 'active',
    currentIssues: 'SDK对接过程中发现设备端编码格式不兼容，需要升级固件',
    currentRequirements: '希望平台提供批量设备管理API',
    pendingTasks: '下周五前完成固件升级测试',
    servicePlan: '每周一跟进SDK对接进度，周三技术会议同步',
    riskNotes: '客户项目上线日期临近，SDK问题可能影响交付',
    tags: {
      serviceContent: ['设备SDK对接', '平台API对接'],
      customerCategory: ['私有定制'],
      capability: ['方案商', '应用服务商'],
      customerTag: ['01战略客户'],
    },
    updatedAt: '2026-07-23 09:30:00', createdAt: '2026-06-01 10:00:00', createdBy: '王主管',
  },
  'test-ent-002': {
    key: '2', enterpriseId: 'test-ent-002', enterpriseName: 'test-广州迅捷科技有限公司', aliasName: '广州迅捷',
    contactPerson: '李四', contactPhone: '13800002222', email: 'lisi@test-xunjie.com',
    authStatus: '已认证',
    background: '设备制造商，主要生产4G摄像头',
    responsibleUserId: 'user-02', responsibleName: '王客服', responsibleAccount: 'wangyuan',
    weight: 80, status: 'active',
    currentIssues: 'License采购流程不熟悉，需要培训',
    currentRequirements: '', pendingTasks: '', servicePlan: '已安排周三下午进行培训', riskNotes: '',
    tags: { serviceContent: ['App OEM定制'], customerCategory: ['公版定制'], capability: ['工厂'], customerTag: ['02大客户'] },
    updatedAt: '2026-07-22 16:20:00', createdAt: '2026-05-15 14:00:00', createdBy: '王主管',
  },
  'test-ent-006': {
    key: '6', enterpriseId: 'test-ent-006', enterpriseName: 'test-杭州联智科技有限公司', aliasName: '杭州联智',
    contactPerson: '周八', contactPhone: '13800006666', email: 'zhouba@test-lianzhi.com',
    authStatus: '已注销', background: '',
    responsibleUserId: 'user-01', responsibleName: '李客服', responsibleAccount: 'lifuwu',
    weight: 10, status: 'archived',
    currentIssues: '', currentRequirements: '', pendingTasks: '', servicePlan: '', riskNotes: '',
    tags: { serviceContent: ['设备SDK对接'], customerCategory: ['维护型'], capability: ['工厂'], customerTag: ['03中等客户'] },
    updatedAt: '2026-07-15 14:30', createdAt: '2026-01-10 10:00', createdBy: '赵主管',
  },
};

// 标签池 Mock
const MOCK_TAG_POOL = {
  '服务内容': ['设备SDK对接', 'AppSDK对接', '平台API对接', 'WEB SDK对接', 'App OEM定制', '云平台私有化部署', '行业SaaS定制'],
  '客户分类': ['公版定制', '标准对接', '私有定制', '维护型'],
  '能力标签': ['工厂', '方案商', '渠道商', '应用服务商'],
  '客户标签': ['01战略客户', '02大客户', '03中等客户'],
};

// 内部账号 Mock
const MOCK_INTERNAL_USERS = [
  { userId: 'user-01', name: '李客服', account: 'lifuwu' },
  { userId: 'user-02', name: '王客服', account: 'wangyuan' },
  { userId: 'user-04', name: '赵主管', account: 'zhaozg' },
  { userId: 'user-05', name: '王主管', account: 'wangzg' },
];

// 项目复盘 Mock
const MOCK_RETROSPECTIVES = [
  {
    key: 'r1', reviewTime: '2026-07-20 14:00', reviewer: '李客服, 王主管',
    problemDescription: 'SDK对接过程中设备端视频编码格式与平台不兼容，导致部分机型出现花屏',
    causeAnalysis: '设备固件使用的H.264编码参数与平台SDK默认配置不一致',
    solution: '1. 更新设备固件编码参数 2. 平台SDK增加编码兼容性检测 3. 发布SDK兼容性对照表',
    lessonsLearned: '提前与客户确认设备编码参数可避免此类问题，建议在对接初期建立技术参数核对清单',
    attachments: ['编码参数对照表_v2.xlsx', '问题截图_20260720.png'],
  },
  {
    key: 'r2', reviewTime: '2026-07-10 10:00', reviewer: '赵主管',
    problemDescription: '客户反馈API调用频率限制影响批量设备管理效率',
    causeAnalysis: '初期API限流策略过于保守，未考虑企业级客户的批量操作需求',
    solution: '调整API限流策略，为企业级客户开放更高的调用频率上限',
    lessonsLearned: 'API限流策略需要按客户等级分级设置，战略客户应享有更宽松的配额',
    attachments: [],
  },
];

// 数据看板 Mock
const MOCK_STATS = {
  deviceBindCount: 1280,
  deviceUnbindCount: 45,
  deviceActivationCount: 320,
  deviceOnlineCount: 1102, // TODO: 联调时按所选时间范围查询在线设备数
  licensePurchaseCount: 156,
  licenseDirectTopupCount: 23,
  vasOrderPayCount: 89,
  vasOrderRefundCount: 6,
  // 增值订单销售额（单位：分）
  vasOrderAmountCny: 3680000,
  vasOrderAmountUsd: 512000,
};

// ══════════════════════════════════════════════
//  常量映射
// ══════════════════════════════════════════════

const STATUS_MAP = { active: { label: '活跃', color: 'blue' }, archived: { label: '归档', color: 'default' } };
const AUTH_STATUS_COLORS = { '已认证': 'green', '待审核': 'orange', '未通过': 'red', '已注销': 'default' };

// ══════════════════════════════════════════════
//  子组件：SectionTitle
// ══════════════════════════════════════════════

const SectionTitle = ({ title }) => (
  <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
    {title}
  </div>
);

// ══════════════════════════════════════════════
//  子组件：复盘编辑弹窗
// ══════════════════════════════════════════════

const RetrospectiveModal = ({ visible, record, onCancel, onSave }) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({ ...record, reviewTime: record.reviewTime ? dayjs(record.reviewTime) : dayjs() });
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({ reviewTime: dayjs() });
    }
  }, [visible, record, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSave?.({
        ...record,
        ...values,
        reviewTime: values.reviewTime?.format('YYYY-MM-DD HH:mm'),
        attachments: values.attachments || [],
      });
      form.resetFields();
    }).catch(() => {});
  };

  return (
    <Modal
      title={record ? '编辑复盘' : '新增复盘'}
      open={visible}
      onCancel={() => { form.resetFields(); onCancel(); }}
      onOk={handleOk}
      width={720}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="reviewTime" label="复盘时间" rules={[{ required: true, message: '请选择复盘时间' }]}>
          <DatePicker showTime style={{ width: '100%' }} disabledDate={(d) => d && d > dayjs()} />
        </Form.Item>
        <Form.Item name="reviewer" label="复盘人员">
          <Input placeholder="多人用逗号分隔" />
        </Form.Item>
        <Form.Item name="problemDescription" label="问题描述" rules={[{ required: true, message: '请输入问题描述' }]}>
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item name="causeAnalysis" label="产生原因">
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item name="solution" label="解决方案">
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item name="lessonsLearned" label="经验与收获">
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item name="attachments" label="附件" valuePropName="fileList" getValueFromEvent={(e) => e?.fileList}>
          <Upload.Dragger multiple beforeUpload={() => false} maxCount={10}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">单文件不超过10MB，支持 jpg/png/pdf/docx/xlsx</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ══════════════════════════════════════════════
//  子组件：复盘详情弹窗
// ══════════════════════════════════════════════

const RetrospectiveDetailModal = ({ visible, record, onClose }) => (
  <Modal title="复盘详情" open={visible} onCancel={onClose} footer={<Button onClick={onClose}>关闭</Button>} width={720}>
    {record && (
      <>
        <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="复盘时间">{record.reviewTime}</Descriptions.Item>
          <Descriptions.Item label="复盘人员">{record.reviewer || '-'}</Descriptions.Item>
        </Descriptions>
        <SectionTitle title="问题描述" />
        <Paragraph>{record.problemDescription || '-'}</Paragraph>
        <SectionTitle title="产生原因" />
        <Paragraph>{record.causeAnalysis || '-'}</Paragraph>
        <SectionTitle title="解决方案" />
        <Paragraph>{record.solution || '-'}</Paragraph>
        <SectionTitle title="经验与收获" />
        <Paragraph>{record.lessonsLearned || '-'}</Paragraph>
        {record.attachments && record.attachments.length > 0 && (
          <>
            <SectionTitle title="附件" />
            <Space wrap>{record.attachments.map((f, i) => <Tag key={i}>{f}</Tag>)}</Space>
          </>
        )}
      </>
    )}
  </Modal>
);

// ══════════════════════════════════════════════
//  主组件：CustomerDetail
// ══════════════════════════════════════════════

const CustomerDetail = () => {
  const { enterpriseId } = useParams();
  const navigate = useNavigate();

  const customer = MOCK_CUSTOMERS[enterpriseId];
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [retrospectives, setRetrospectives] = useState(MOCK_RETROSPECTIVES);
  const [retroModalVisible, setRetroModalVisible] = useState(false);
  const [retroEditRecord, setRetroEditRecord] = useState(null);
  const [retroDetailVisible, setRetroDetailVisible] = useState(false);
  const [retroDetailRecord, setRetroDetailRecord] = useState(null);
  const [statsTimeRange, setStatsTimeRange] = useState('30d');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState({});
  const [tagsState, setTagsState] = useState(customer?.tags || {});

  if (!customer) {
    return (
      <div style={{ padding: 24 }}>
        <Result status="404" title="客户不存在" subTitle="未找到该客户跟踪记录" extra={<Button type="primary" onClick={() => navigate('/customer/crm')}>返回列表</Button>} />
      </div>
    );
  }

  const isArchived = customer.status === 'archived';
  const isEnterpriseCancelled = customer.authStatus === '已注销';

  const handleEdit = () => {
    form.setFieldsValue({
      enterpriseName: customer.enterpriseName,
      aliasName: customer.aliasName || '',
      contactPerson: customer.contactPerson,
      contactPhone: customer.contactPhone,
      email: customer.email,
      background: customer.background,
      responsibleUserId: customer.responsibleUserId,
      weight: customer.weight,
      currentIssues: customer.currentIssues,
      currentRequirements: customer.currentRequirements,
      pendingTasks: customer.pendingTasks,
      servicePlan: customer.servicePlan,
      riskNotes: customer.riskNotes,
    });
    setTagsState({ ...customer.tags });
    setEditing(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      // TODO: 联调时替换为接口保存，将 values + tagsState 一并提交
      message.success('保存成功');
      setEditing(false);
    }).catch(() => {});
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setTagsState(customer.tags);
    form.resetFields();
  };

  const handleArchive = () => {
    Modal.confirm({
      title: '确定归档该客户？',
      content: '归档后跟踪记录将只读。',
      onOk: () => message.success('已归档'),
    });
  };

  const handleRestore = () => {
    Modal.confirm({
      title: '确定恢复该客户？',
      content: '恢复后可继续编辑。',
      onOk: () => message.success('已恢复'),
    });
  };

  // 复盘操作
  const handleRetroSave = (values) => {
    if (retroEditRecord?.key) {
      setRetrospectives((prev) => prev.map((r) => (r.key === retroEditRecord.key ? { ...r, ...values } : r)));
    } else {
      setRetrospectives((prev) => [{ key: `r${Date.now()}`, ...values }, ...prev]);
    }
    setRetroModalVisible(false);
    setRetroEditRecord(null);
    message.success('复盘记录已保存');
  };

  const handleRetroDelete = (record) => {
    setRetrospectives((prev) => prev.filter((r) => r.key !== record.key));
    message.success('已删除');
  };

  const handleStatsRefresh = () => {
    setStatsLoading(true);
    setStatsError({});
    setTimeout(() => setStatsLoading(false), 800);
  };

  const renderTagGroup = (categoryName, tagKey, options) => {
    const currentTags = editing ? (tagsState[tagKey] || []) : (customer.tags[tagKey] || []);
    return (
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 13, marginRight: 8, fontWeight: 500 }}>{categoryName}</Text>
        {editing ? (
          <Select
            mode="multiple"
            style={{ width: '100%', marginTop: 8 }}
            placeholder={`选择${categoryName}`}
            value={currentTags}
            onChange={(vals) => setTagsState((prev) => ({ ...prev, [tagKey]: vals }))}
          >
            {options.map((t) => <Option key={t} value={t}>{t}</Option>)}
          </Select>
        ) : (
          <Space size={4} wrap style={{ display: 'inline-flex' }}>
            {currentTags.length > 0 ? currentTags.map((t) => <Tag key={t} color="blue" style={{ border: 'none' }}>{t}</Tag>) : <span style={{ color: '#ccc' }}>-</span>}
          </Space>
        )}
      </div>
    );
  };

  const tabItems = [
    // Tab 1: 客户资料
    {
      key: 'info',
      label: '客户资料',
      children: (
        <div style={{ maxWidth: 900 }}>
          {/* 编辑/保存/取消按钮 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div />
            <Space>
              {editing ? (
                <>
                  <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>取消</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>保存</Button>
                </>
              ) : (
                !isArchived && <Button icon={<EditOutlined />} onClick={handleEdit}>编辑</Button>
              )}
            </Space>
          </div>
          {/* 区块A：企业基础信息 */}
          <div style={{ background: '#f9f9f9', padding: 20, borderRadius: 8, marginBottom: 24 }}>
            <SectionTitle title="企业基础信息" />
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="企业名称">
                {isEnterpriseCancelled ? <Text delete>{customer.enterpriseName}</Text> : (
                  editing ? <Form.Item name="enterpriseName" noStyle><Input /></Form.Item> : customer.enterpriseName
                )}
                {isEnterpriseCancelled && <Tag color="default" style={{ marginLeft: 8 }}>已注销</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="企业ID">{customer.enterpriseId}</Descriptions.Item>
              <Descriptions.Item label="企业别名">
                {editing ? <Form.Item name="aliasName" noStyle><Input placeholder="请输入企业别名" /></Form.Item> : (customer.aliasName || '-')}
              </Descriptions.Item>
              <Descriptions.Item label="联系人">
                {editing ? <Form.Item name="contactPerson" noStyle><Input /></Form.Item> : customer.contactPerson}
              </Descriptions.Item>
              <Descriptions.Item label="联系电话">
                {editing ? <Form.Item name="contactPhone" noStyle><Input /></Form.Item> : customer.contactPhone}
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">
                {editing ? <Form.Item name="email" noStyle><Input /></Form.Item> : customer.email}
              </Descriptions.Item>
              <Descriptions.Item label="认证状态">
                <Tag color={AUTH_STATUS_COLORS[customer.authStatus]} style={{ border: 'none' }}>{customer.authStatus}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="背景介绍" span={2}>
                {editing ? (
                  <Form.Item name="background" noStyle><TextArea rows={2} /></Form.Item>
                ) : (customer.background || <span style={{ color: '#ccc' }}>-</span>)}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* 区块B：标签 */}
          <div style={{ marginBottom: 24 }}>
            <SectionTitle title="标签" />
            {Object.entries(MOCK_TAG_POOL).map(([categoryName, options]) => {
              const tagKey = categoryName === '服务内容' ? 'serviceContent' : categoryName === '客户分类' ? 'customerCategory' : categoryName === '能力标签' ? 'capability' : 'customerTag';
              return (
                <div key={categoryName}>
                  {renderTagGroup(categoryName, tagKey, options)}
                </div>
              );
            })}
          </div>

          {/* 区块C：服务跟踪 */}
          <div style={{ marginBottom: 24 }}>
            <SectionTitle title="服务跟踪" />
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="责任人">
                {editing ? (
                  <Form.Item name="responsibleUserId" noStyle rules={[{ required: true, message: '请选择责任人' }]}>
                    <Select placeholder="搜索选择责任人" showSearch optionFilterProp="children" style={{ width: '100%' }}>
                      {MOCK_INTERNAL_USERS.map((u) => <Option key={u.userId} value={u.userId}>{u.name} / {u.account}</Option>)}
                    </Select>
                  </Form.Item>
                ) : (
                  <Space direction="vertical" size={0}>
                    <Text>{customer.responsibleName}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{customer.responsibleAccount}</Text>
                  </Space>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="权重">
                {editing ? (
                  <Form.Item name="weight" noStyle>
                    <Space>
                      <InputNumber min={0} max={100} style={{ width: 80 }} />
                      <Slider min={0} max={100} style={{ width: 120, margin: 0 }} />
                    </Space>
                  </Form.Item>
                ) : (
                  <Tag color={customer.weight >= 80 ? 'red' : customer.weight >= 50 ? 'orange' : 'blue'}>{customer.weight}</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="当前重要问题" span={2}>
                {editing ? <Form.Item name="currentIssues" noStyle><TextArea rows={3} /></Form.Item> : (customer.currentIssues || '-')}
              </Descriptions.Item>
              <Descriptions.Item label="当前重要需求" span={2}>
                {editing ? <Form.Item name="currentRequirements" noStyle><TextArea rows={3} /></Form.Item> : (customer.currentRequirements || '-')}
              </Descriptions.Item>
              <Descriptions.Item label="待交付任务" span={2}>
                {editing ? <Form.Item name="pendingTasks" noStyle><TextArea rows={3} /></Form.Item> : (customer.pendingTasks || '-')}
              </Descriptions.Item>
              <Descriptions.Item label="服务计划" span={2}>
                {editing ? <Form.Item name="servicePlan" noStyle><TextArea rows={3} /></Form.Item> : (customer.servicePlan || '-')}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {/* 风险备注 — 醒目独立区块 */}
          <div style={{
            background: customer.riskNotes ? '#fff7e6' : '#fafafa',
            border: customer.riskNotes ? '1px solid #ffd591' : '1px solid #f0f0f0',
            borderRadius: 8, padding: 16, marginBottom: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: editing ? 12 : (customer.riskNotes ? 8 : 0) }}>
              <WarningOutlined style={{ color: customer.riskNotes ? '#fa8c16' : '#d9d9d9', fontSize: 16, marginRight: 8 }} />
              <Text strong style={{ fontSize: 14 }}>风险备注</Text>
            </div>
            {editing ? (
              <Form.Item name="riskNotes" noStyle><TextArea rows={3} placeholder="记录客户当前存在的风险" /></Form.Item>
            ) : (
              customer.riskNotes ? (
                <Text style={{ color: '#d46b08', fontSize: 14, lineHeight: 1.8 }}>{customer.riskNotes}</Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>暂无风险记录</Text>
              )
            )}
          </div>

          {/* 区块D：元信息 */}
          <div>
            <SectionTitle title="元信息" />
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="创建时间">{customer.createdAt}</Descriptions.Item>
              <Descriptions.Item label="创建人">{customer.createdBy}</Descriptions.Item>
              <Descriptions.Item label="最近更新">{customer.updatedAt}</Descriptions.Item>
              <Descriptions.Item label="更新人">{customer.updatedBy || customer.createdBy}</Descriptions.Item>
            </Descriptions>
          </div>
        </div>
      ),
    },

    // Tab 2: 已对接产品
    {
      key: 'products',
      label: '已对接产品',
      children: (
        <ConnectedProductsTab
          enterpriseId={customer.enterpriseId}
          enterpriseName={customer.enterpriseName}
        />
      ),
    },

    // Tab 3: 数据看板
    {
      key: 'stats',
      label: '数据看板',
      children: (
        <div>
          {/* 时间筛选 */}
          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Text strong>时间范围：</Text>
            <Select value={statsTimeRange} onChange={setStatsTimeRange} style={{ width: 140 }}>
              <Option value="7d">近7天</Option>
              <Option value="30d">近30天</Option>
              <Option value="90d">近90天</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={handleStatsRefresh}>刷新</Button>
          </div>

          <Spin spinning={statsLoading}>
            <Row gutter={[16, 16]}>
              {[
                { label: '设备绑定数', value: MOCK_STATS.deviceBindCount, key: 'bind' },
                { label: '设备解绑数', value: MOCK_STATS.deviceUnbindCount, key: 'unbind' },
                { label: '设备激活数', value: MOCK_STATS.deviceActivationCount, key: 'activation' },
                { label: '设备在线数', value: MOCK_STATS.deviceOnlineCount, key: 'deviceOnline' },
              ].map((item) => (
                <Col span={6} key={item.key}>
                  <Card
                    bordered={false}
                    style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', textAlign: 'center' }}
                  >
                    {statsError[item.key] ? (
                      <div style={{ color: '#999' }}>
                        <WarningOutlined style={{ fontSize: 20, marginBottom: 8 }} />
                        <div>获取失败</div>
                        <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); handleStatsRefresh(); }}>重试</Button>
                      </div>
                    ) : (
                      <>
                        <Text type="secondary">{item.label}</Text>
                        <div style={{ fontSize: 32, fontWeight: 600, color: '#1677ff', margin: '8px 0' }}>{item.value.toLocaleString()}</div>
                      </>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              {[
                { label: 'License 采购数', value: MOCK_STATS.licensePurchaseCount, key: 'licensePurchase' },
                { label: 'License 直充数', value: MOCK_STATS.licenseDirectTopupCount, key: 'licenseTopup' },
                { label: 'App订单', value: `支付 ${MOCK_STATS.vasOrderPayCount} 笔 / 退款 ${MOCK_STATS.vasOrderRefundCount} 笔`, key: 'vasOrders' },
                { label: '增值订单销售额', value: `¥ ${(MOCK_STATS.vasOrderAmountCny / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / $ ${(MOCK_STATS.vasOrderAmountUsd / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, key: 'vasSales', isAmount: true },
              ].map((item) => (
                <Col span={6} key={item.key}>
                  <Card
                    bordered={false}
                    style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', textAlign: 'center' }}
                  >
                    <Text type="secondary">{item.label}</Text>
                    <div style={{ fontSize: item.isAmount ? 24 : 32, fontWeight: 600, color: '#1677ff', margin: '8px 0' }}>{item.value.toLocaleString()}</div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Spin>
        </div>
      ),
    },

    // Tab 3: 项目复盘
    {
      key: 'retrospective',
      label: '项目复盘',
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={isArchived}
              onClick={() => { setRetroEditRecord(null); setRetroModalVisible(true); }}
            >
              新增复盘
            </Button>
          </div>
          <Table
            columns={[
              { title: '复盘时间', dataIndex: 'reviewTime', width: 140, sorter: (a, b) => a.reviewTime.localeCompare(b.reviewTime), defaultSortOrder: 'descend' },
              { title: '复盘人员', dataIndex: 'reviewer', width: 120, render: (t) => t || '-' },
              { title: '问题描述', dataIndex: 'problemDescription', width: 180, ellipsis: true, render: (t) => <Tooltip title={t}>{t}</Tooltip> },
              { title: '产生原因', dataIndex: 'causeAnalysis', width: 140, ellipsis: true, render: (t) => <Tooltip title={t}>{t || '-'}</Tooltip> },
              { title: '解决方案', dataIndex: 'solution', width: 140, ellipsis: true, render: (t) => <Tooltip title={t}>{t || '-'}</Tooltip> },
              {
                title: '操作', key: 'actions', width: 100, fixed: 'right',
                render: (_, record) => (
                  <Space size={0} split={<span style={{ color: '#e8e8e8' }}>|</span>}>
                    <Button type="link" size="small" onClick={() => { setRetroDetailRecord(record); setRetroDetailVisible(true); }}>查看</Button>
                    {!isArchived && (
                      <>
                        <Button type="link" size="small" onClick={() => { setRetroEditRecord(record); setRetroModalVisible(true); }}>编辑</Button>
                        <Popconfirm title="确定删除？不可恢复" onConfirm={() => handleRetroDelete(record)}>
                          <Button type="link" size="small" danger>删除</Button>
                        </Popconfirm>
                      </>
                    )}
                  </Space>
                ),
              },
            ]}
            dataSource={retrospectives}
            scroll={{ x: 720 }}
            pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
            locale={{ emptyText: <Empty description="暂无项目复盘记录" /> }}
          />

          {/* 弹窗 */}
          <RetrospectiveModal
            visible={retroModalVisible}
            record={retroEditRecord}
            onCancel={() => { setRetroModalVisible(false); setRetroEditRecord(null); }}
            onSave={handleRetroSave}
          />
          <RetrospectiveDetailModal
            visible={retroDetailVisible}
            record={retroDetailRecord}
            onClose={() => { setRetroDetailVisible(false); setRetroDetailRecord(null); }}
          />
        </div>
      ),
    },

    // Tab 5: 客户工单
    {
      key: 'tickets',
      label: '客户工单',
      children: (
        <CustomerTicketsTab
          enterpriseId={customer.enterpriseId}
          isArchived={isArchived}
        />
      ),
    },

    // Tab 6: 出货计划
    {
      key: 'shipment',
      label: '出货计划',
      children: (
        <ShipmentPlanTab
          mode="platform"
          enterpriseId={customer.enterpriseId}
          isArchived={isArchived}
        />
      ),
    },

    // Tab 7: 服务排期
    {
      key: 'schedule',
      label: '服务排期',
      children: (
        <ServiceScheduleTab mode="platform" isArchived={isArchived} />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* 页面头部 */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer/crm')}
          style={{ padding: 0, marginBottom: 12 }}
        >
          返回列表
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isEnterpriseCancelled ? <Text delete>{customer.enterpriseName}</Text> : customer.enterpriseName}
              <Tag color={AUTH_STATUS_COLORS[customer.authStatus]} style={{ marginLeft: 12, border: 'none' }}>{customer.authStatus}</Tag>
            </Title>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">企业ID: {customer.enterpriseId}</Text>
              <Tag color={STATUS_MAP[customer.status]?.color} style={{ marginLeft: 16, border: 'none' }}>{STATUS_MAP[customer.status]?.label}</Tag>
            </div>
          </div>
          <Space>
            {isArchived ? (
              <>
                <Button icon={<UndoOutlined />} onClick={handleRestore}>恢复</Button>
              </>
            ) : (
              <Button icon={<FileTextOutlined />} onClick={handleArchive}>归档</Button>
            )}
          </Space>
        </div>
      </div>

      {/* Tab 内容 */}
      <Form form={form} layout="vertical" component={false}>
        <Tabs items={tabItems} defaultActiveKey="info" />
      </Form>
    </div>
  );
};

export default CustomerDetail;
